import { useEffect, useMemo } from 'react';
import { useLocation, useHref } from 'react-router-dom-v5-compat';
import NavigationActions from '../../../Actions/NavigationActions';
import { produce } from 'immer';

export type NavigationHookArgs = {
    title: string;
    breadcrumbs?: {
        name: string;
        url: string;
    }[];
    helpLink?: string;
};

export function useNavigationOccurred(args: NavigationHookArgs) {
    const location = useLocation();
    const pathnameWithAppBase = useHref(location.pathname);
    const locationWithAppBase = useMemo(
        () => produce(location, draft => {
            draft.pathname = pathnameWithAppBase; // prepend the base URL to the pathname
        }),
        [ location, pathnameWithAppBase ]
    );

    const { title, breadcrumbs, helpLink } = args;

    useEffect(() => {
        NavigationActions.routeChanged(locationWithAppBase);
    }, [ locationWithAppBase ]);

    useEffect(() => {
        // Deliberately keyed on the pathname rather than the whole location so that navigation which only changes the
        // query string (e.g. switching tabs within a page) does not overwrite a title/breadcrumbs the page itself has
        // set. Effects of child components run before those of their parents, so re-running this on every location
        // change would always clobber the page's own values.
        NavigationActions.setPageTitle(title);
        NavigationActions.setBreadcrumbs(breadcrumbs);
        NavigationActions.setPageHelpLink(helpLink);
    }, [ pathnameWithAppBase, title, breadcrumbs, helpLink ]);
}
